import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { selectedLocationKey } from "@/lib/storage-keys";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { takePrefetchedLocations } from "@/lib/startup-prefetch";

// Filtro operativo GLOBALE per sede (multi-sede — Fase 5). A 0 o 1 sola sede
// attiva questo context esiste ma resta invisibile: nessuna pagina mostra un
// selettore, `isMultiLocation` è false e `selectedLocationId` resta "all"
// senza filtrare nulla — stessa UX di oggi per la stragrande maggioranza dei
// business esistenti.

const LocationContext = createContext(null);

function storageKey(businessId) {
  return selectedLocationKey(businessId);
}

export function LocationProvider({ children }) {
  const { business } = useAuth();
  const businessId = business?.business_id || null;
  const [locations, setLocations] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [selectedLocationId, setSelectedLocationIdState] = useState("all");

  const refresh = useCallback(async () => {
    if (!businessId) return;
    try {
      // All'avvio della dashboard la richiesta e' gia' partita in parallelo a
      // /auth/me (lib/startup-prefetch.js). Se e' fallita si riprova normalmente.
      const pre = takePrefetchedLocations();
      let r;
      try { r = pre ? await pre : await api.get("/locations"); } catch (e) { if (!pre) throw e; r = await api.get("/locations"); }
      setLocations(r.data || []);
    } catch {
      setLocations([]);
    } finally {
      setLoaded(true);
    }
  }, [businessId]);

  useEffect(() => {
    if (!businessId) {
      setLocations([]);
      setLoaded(false);
      setSelectedLocationIdState("all");
      return;
    }
    // Selezione persistita per business: mai condivisa fra business diversi
    // sullo stesso browser.
    try {
      setSelectedLocationIdState(window.localStorage.getItem(storageKey(businessId)) || "all");
    } catch {
      setSelectedLocationIdState("all");
    }
    refresh();
  }, [businessId, refresh]);

  const setSelectedLocationId = useCallback(
    (id) => {
      setSelectedLocationIdState(id);
      if (!businessId) return;
      try {
        window.localStorage.setItem(storageKey(businessId), id);
      } catch {
        /* privacy mode: la selezione resta solo per questa sessione */
      }
    },
    [businessId],
  );

  const activeLocations = useMemo(() => locations.filter((l) => l.enabled !== false), [locations]);
  const isMultiLocation = activeLocations.length > 1;

  // Una sede scelta in passato può essere stata disattivata nel frattempo:
  // ricadi silenziosamente su "Tutte le sedi" invece di restare su un id morto.
  useEffect(() => {
    if (
      loaded &&
      selectedLocationId !== "all" &&
      !activeLocations.some((l) => l.location_id === selectedLocationId)
    ) {
      setSelectedLocationId("all");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded, activeLocations, selectedLocationId]);

  const selectedLocation =
    selectedLocationId === "all" ? null : activeLocations.find((l) => l.location_id === selectedLocationId) || null;

  return (
    <LocationContext.Provider
      value={{
        locations,
        activeLocations,
        loaded,
        isMultiLocation,
        selectedLocationId,
        setSelectedLocationId,
        selectedLocation,
        refresh,
      }}
    >
      {children}
    </LocationContext.Provider>
  );
}

export function useLocations() {
  const ctx = useContext(LocationContext);
  if (!ctx) throw new Error("useLocations deve essere usato dentro LocationProvider");
  return ctx;
}
